import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import getRaffleConfigs from 'pages/Raffle/Raffle.config';

export type RaffleCart = Record<number, number>;

export type AddedToCartHighlightStatus = 'visible' | 'viewed' | 'hidden';

type TRaffleCartContext = {
  raffleBasket: RaffleCart;
  totalBasketItemsCount: number;
  setBasket: (cart: RaffleCart) => void;
  updateBasket: (id: number, quantity: number) => void;
  isTotalBasketItemsCountReached: boolean;
  isLoading: boolean;
  addedToCartHighlight: {
    status: AddedToCartHighlightStatus;
    close: () => void;
  };
  hasUserIdentityModalShowed: boolean;
  updateHasUserIdentityModalShowed: () => void;
};

export const RaffleCartContext = createContext<TRaffleCartContext>(
  {} as TRaffleCartContext
);
export const RaffleCartProvider: React.FC = ({ children }) => {
  const [hasUserIdentityModalShowed, setHasUserIdentityModalShowed] = useState(
    false
  );
  const [
    addedToCartHighlightStatus,
    setAddedToCartHighlightStatus,
  ] = useState<AddedToCartHighlightStatus>('hidden');
  const [cart, setCart] = useState<RaffleCart>({});
  const [isLoading, setIsLoading] = useState(false);
  const [totalBasketItemsCount, setTotalBasketItemsCount] = useState(0);
  const { maxCartItemQuantity } = getRaffleConfigs();

  function updateHasUserIdentityModalShowed() {
    setHasUserIdentityModalShowed(true);
  }

  function setBasket(cartItems: RaffleCart) {
    setCart(cartItems);
  }

  function updateBasket(drawId: number, quantity: number) {
    if (quantity === 0) {
      setIsLoading(true);
      const { [drawId]: removedItem, ...others } = cart;
      setCart({ ...others });
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    } else {
      setCart({ ...cart, [drawId]: quantity });
    }
  }

  useEffect(() => {
    const totalItems = Object.values(cart).reduce((sum, value) => {
      return sum + value;
    }, 0);
    if (
      addedToCartHighlightStatus === 'hidden' &&
      totalBasketItemsCount === 0 &&
      totalItems > 0
    ) {
      setAddedToCartHighlightStatus('visible');
    }
    setTotalBasketItemsCount(totalItems);
  }, [cart]);

  const isTotalBasketItemsCountReached = useMemo(() => {
    return totalBasketItemsCount === maxCartItemQuantity;
  }, [totalBasketItemsCount]);

  const item = {
    raffleBasket: cart,
    totalBasketItemsCount,
    setBasket,
    updateBasket,
    isLoading,
    isTotalBasketItemsCountReached,
    hasUserIdentityModalShowed,
    addedToCartHighlight: {
      status: addedToCartHighlightStatus,
      close: () => setAddedToCartHighlightStatus('viewed'),
    },
    updateHasUserIdentityModalShowed,
  };

  return (
    <RaffleCartContext.Provider value={item}>
      {children}
    </RaffleCartContext.Provider>
  );
};

export function useRaffleCart(): TRaffleCartContext {
  return useContext(RaffleCartContext);
}
